// Floating Action Button for mobile quick posting
// Shows on mobile only, opens QuickPostForm in a dialog

import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Pencil } from 'lucide-react'
import { QuickPostForm } from './QuickPostForm'

export function FloatingActionButton() {
  const [open, setOpen] = useState(false)
  const [viewportHeight, setViewportHeight] = useState<number | null>(null)

  // Handle viewport resize (keyboard open/close)
  const updateViewportHeight = useCallback(() => {
    if (window.visualViewport) {
      setViewportHeight(window.visualViewport.height)
    }
  }, [])

  // Prevent background scroll and track viewport changes when dialog is open
  useEffect(() => {
    if (open) {
      const scrollY = window.scrollY
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
      document.body.style.overflow = 'hidden'

      // Initialize viewport height asynchronously to avoid synchronous setState in effect
      const timeoutId = requestAnimationFrame(updateViewportHeight)

      // Listen for viewport changes (keyboard open/close)
      window.visualViewport?.addEventListener('resize', updateViewportHeight)

      return () => {
        cancelAnimationFrame(timeoutId)
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        document.body.style.overflow = ''
        window.scrollTo(0, scrollY)
        window.visualViewport?.removeEventListener('resize', updateViewportHeight)
        setViewportHeight(null)
      }
    }
  }, [open, updateViewportHeight])

  // Calculate max height for dialog content (viewport height - padding)
  const maxHeight = viewportHeight ? `${viewportHeight - 32}px` : undefined

  return (
    <>
      {/* FAB - visible only on mobile */}
      <Button
        size="icon"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full shadow-lg sm:hidden"
        aria-label="Quick post"
      >
        <Pencil className="h-6 w-6" />
      </Button>

      {/* Quick Post Dialog - positioned at top of screen */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="top-4 translate-y-0 max-w-[calc(100%-2rem)] sm:max-w-lg flex flex-col"
          style={{ maxHeight }}
          showCloseButton={false}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Quick Post</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0">
            <QuickPostForm onSuccess={() => setOpen(false)} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
